import * as React from "react"

import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Formula-aware numeric inputs
// ---------------------------------------------------------------------------
// When `type === "number"`, the underlying <input> is rendered as text so the
// user can type spreadsheet-style expressions like `1+4+5`, `10/5`, or
// `(2+3)*4`. The expression is evaluated on blur, on `Enter`, or as soon as
// the user types `=`, replacing the field with the numeric result.
//
// • While the user is typing, plain-number patterns (e.g. `12.34`) flow
//   through to the parent's `onChange` immediately so React-controlled
//   state stays in sync. Anything else (operators, parentheses) stays in
//   a local draft until evaluation.
// • Evaluation uses `Function("return (...)")` guarded by a strict regex
//   that only allows digits, decimal points, basic arithmetic operators,
//   parentheses, commas (stripped) and spaces. Anything outside this
//   alphabet falls through unchanged so junk text like `abc` is just
//   passed through (and the parent's `parseFloat` handles it as before).
// • Opt out per-input by passing `noFormula`.
// ---------------------------------------------------------------------------

const SAFE_FORMULA_RE = /^[\d+\-*/(). ,]+$/
const NUMBER_RE = /^-?\d*\.?\d*$/

const evalFormula = (raw) => {
  const trimmed = String(raw ?? "").replace(/,/g, "").trim()
  if (!trimmed) return ""
  // If it's already a plain number, no work to do.
  if (NUMBER_RE.test(trimmed)) return trimmed
  // Reject anything that contains characters outside the safe alphabet.
  if (!SAFE_FORMULA_RE.test(trimmed)) return raw
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function("return (" + trimmed + ")")
    const v = fn()
    if (typeof v === "number" && Number.isFinite(v)) {
      // Trim trailing zeros: 4.75 stays, 2.0 → "2".
      return Number.isInteger(v) ? String(v) : String(parseFloat(v.toFixed(10)))
    }
  } catch { /* ignore — return raw below */ }
  return raw
}

const FormulaNumberInput = React.forwardRef(
  ({ className, value, onChange, onBlur, onKeyDown, name, ...props }, ref) => {
    const [draft, setDraft] = React.useState(value == null ? "" : String(value))
    // Keep the local draft in sync when the parent resets / changes value
    // externally (e.g. form reset, autoload from API).
    const lastExternalRef = React.useRef(value)
    React.useEffect(() => {
      if (value !== lastExternalRef.current) {
        lastExternalRef.current = value
        setDraft(value == null ? "" : String(value))
      }
    }, [value])

    // Build a change-event shape that matches React's SyntheticEvent well
    // enough for parent handlers that do `const {name, value} = e.target` —
    // crucial for forms that share a single handleInputChange across many
    // inputs (TransferEntry.jsx, etc.). Without `name` here, the parent
    // writes into `formData[undefined]` and the field appears broken.
    const fire = (newVal) => {
      if (!onChange) return
      onChange({ target: { name, value: newVal } })
    }

    const commit = (raw) => {
      const evaluated = evalFormula(raw)
      if (evaluated !== raw) {
        setDraft(evaluated)
      }
      fire(evaluated)
    }

    return (
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        name={name}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        value={draft}
        onChange={(e) => {
          const v = e.target.value
          setDraft(v)
          // Live-propagate plain numbers so React state stays in sync while
          // the user is typing simple values; expressions wait until blur.
          if (NUMBER_RE.test(v)) {
            fire(v)
          }
        }}
        onBlur={(e) => {
          commit(draft)
          onBlur && onBlur(e)
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            commit(draft)
          } else if (e.key === "=") {
            // Typing `=` after an expression is a familiar Excel shortcut to
            // evaluate immediately.
            e.preventDefault()
            commit(draft)
          }
          onKeyDown && onKeyDown(e)
        }}
        {...props}
      />
    )
  }
)
FormulaNumberInput.displayName = "FormulaNumberInput"

const Input = React.forwardRef(({ className, type, noFormula, ...props }, ref) => {
  // Numeric inputs get the formula-aware variant unless explicitly opted out.
  if (type === "number" && !noFormula) {
    return <FormulaNumberInput ref={ref} className={className} {...props} />
  }
  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
